/**
 * posFuzzyEngine.js
 * Motor otimizado de busca fonética e tolerância a erros ortográficos (Fuzzy)
 * adaptado para a realidade do varejo brasileiro e comércio no PDV.
 *
 * Casos cobertos:
 * - Estrangeirismos e sons de H / R: "reineken" <-> "heineken", "rotdog" <-> "hot dog"
 * - Troca de vogais átonas O/U: "juelho" <-> "joelho", "tumate" <-> "tomate", "bulacha" <-> "bolacha"
 * - Troca de vogais átonas E/I: "mantiga" <-> "manteiga", "sirveja" <-> "cerveja"
 * - Fonemas sibilantes: S, C (e/i), Ç, Z, SS, X ("asucar" <-> "açúcar", "salxixa" <-> "salsicha", "sebola" <-> "cebola")
 * - Velares: K, QU, C (a/o/u), CK ("ketchup" <-> "quetchup", "koka" <-> "coca")
 * - Palatais: J, G (e/i) ("jelo" <-> "gelo", "jiló" <-> "gilo")
 * - Letras dobradas e encontros consonantais
 */

/**
 * Remove acentos e caracteres diacríticos, convertendo para minúsculas
 */
const normalizeRawText = (text) => {
  if (!text) return '';
  return String(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

/**
 * Converte uma palavra para sua representação fonética simplificada PT-BR.
 * Gera uma string canônica que unifica sons idênticos ou frequentemente trocados.
 */
const getPhoneticKey = (word) => {
  if (!word) return '';
  let str = normalizeRawText(word);

  // Remove caracteres que não sejam letras ou números
  str = str.replace(/[^a-z0-9]/g, '');
  if (!str) return '';

  // 1. Estrangeirismos com H inicial: no Brasil, marcas e palavras em inglês
  // iniciadas por H (ex: Heineken, Hot dog, Headset) têm som aspirado de 'R'.
  // Mapeamos 'h' no início para 'r'.
  if (str.startsWith('h')) {
    str = 'r' + str.slice(1);
  }

  // 2. Ditongos fonéticos comuns em marcas / estrangeirismos (som /ai/ em Heineken, Nike, etc.)
  str = str.replace(/ei/g, 'ai');

  // 3. Dígrafos e encontros consonantais
  str = str.replace(/ph/g, 'f');
  str = str.replace(/ch/g, 'x');
  str = str.replace(/sh/g, 'x');
  str = str.replace(/ck/g, 'k');
  str = str.replace(/qu([ei])/g, 'k$1');
  str = str.replace(/qu([ao])/g, 'ku$1');
  str = str.replace(/sc([ei])/g, 's$1');
  str = str.replace(/xc([ei])/g, 's$1');

  // 4. Consoantes velares: c antes de a, o, u -> k
  str = str.replace(/c([aou])/g, 'k$1');
  str = str.replace(/c$/g, 'k');
  str = str.replace(/c([^eiaou])/g, 'k$1');

  // 5. Sibilantes: c antes de e, i -> s; ç -> s; z -> s
  str = str.replace(/c([ei])/g, 's$1');
  str = str.replace(/ç/g, 's');
  str = str.replace(/z/g, 's');

  // 6. Palatais: g antes de e, i -> j
  str = str.replace(/g([ei])/g, 'j$1');

  // 7. Vogais átonas e aproximações fonéticas comuns em PT-BR
  // o <-> u
  str = str.replace(/o/g, 'u');
  // e <-> i
  str = str.replace(/e/g, 'i');
  // y <-> i, w <-> v
  str = str.replace(/y/g, 'i');
  str = str.replace(/w/g, 'v');

  // 8. Supressão de consoantes dobradas (ss -> s, rr -> r, nn -> n, etc.)
  str = str.replace(/(.)\1+/g, '$1');

  // 9. Troca de l final com som de u (ex: pastel -> pasteu -> pastiu)
  str = str.replace(/l$/g, 'u');

  // 10. Supressão de nasal átona final (m/n) em palavras longas (ex: raineken -> raineke, heineken -> heineke)
  if (str.length >= 5 && /[nm]$/.test(str)) {
    str = str.slice(0, -1);
  }

  return str;
};

/**
 * Calcula a distância de Damerau-Levenshtein entre duas strings
 * (inserção, deleção, substituição e transposição de caracteres adjacentes).
 */
const damerauLevenshteinDistance = (source, target) => {
  if (!source) return target ? target.length : 0;
  if (!target) return source.length;

  const srcLen = source.length;
  const tgtLen = target.length;
  const matrix = Array.from({ length: srcLen + 1 }, () => new Array(tgtLen + 1).fill(0));

  for (let i = 0; i <= srcLen; i++) matrix[i][0] = i;
  for (let j = 0; j <= tgtLen; j++) matrix[0][j] = j;

  for (let i = 1; i <= srcLen; i++) {
    for (let j = 1; j <= tgtLen; j++) {
      const cost = source[i - 1] === target[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1, // deleção
        matrix[i][j - 1] + 1, // inserção
        matrix[i - 1][j - 1] + cost // substituição
      );

      // Transposição
      if (
        i > 1 &&
        j > 1 &&
        source[i - 1] === target[j - 2] &&
        source[i - 2] === target[j - 1]
      ) {
        matrix[i][j] = Math.min(matrix[i][j], matrix[i - 2][j - 2] + 1);
      }
    }
  }

  return matrix[srcLen][tgtLen];
};

/**
 * Retorna uma pontuação de similaridade de 0.0 a 1.0 entre duas strings.
 */
const getSimilarityScore = (strA, strB) => {
  if (!strA || !strB) return 0;
  if (strA === strB) return 1;

  const maxLen = Math.max(strA.length, strB.length);
  if (maxLen === 0) return 1;

  const dist = damerauLevenshteinDistance(strA, strB);
  return Math.max(0, 1 - dist / maxLen);
};

/**
 * Constrói o índice de busca fonética para um item do catálogo.
 * Essa função é chamada para pré-indexar cada produto/serviço uma única vez na memória.
 */
const buildSearchItemIndex = (item) => {
  const rawName = item.name || '';
  const cleanName = normalizeRawText(rawName);
  const words = cleanName.split(/\s+/).filter(Boolean);

  const phoneticTokens = words.map((w) => getPhoneticKey(w)).filter(Boolean);
  const barCode = item.barCode ? String(item.barCode).trim().toLowerCase() : '';
  const idStr = String(item.id || '');
  const categoryIdStr = item.categoryId
    ? String(item.categoryId)
    : (item.category && typeof item.category === 'object' && item.category.id !== undefined
      ? String(item.category.id)
      : (item.category !== null && item.category !== undefined ? String(item.category) : ''));
  const categoryName =
    item.categoryName ||
    (typeof item.category === 'object' && item.category && item.category.name
      ? item.category.name
      : '');
  const cleanCategoryName = normalizeRawText(categoryName);

  return {
    ...item,
    categoryId: categoryIdStr || item.categoryId,
    categoryName: categoryName || item.categoryName,
    _cleanName: cleanName,
    _words: words,
    _phoneticTokens: phoneticTokens,
    _barCode: barCode,
    _idStr: idStr,
    _categoryIdStr: categoryIdStr,
    _categoryName: cleanCategoryName,
  };
};

/**
 * Filtra e ranqueia produtos e serviços com base no termo digitado.
 * Utiliza o pipeline em cascata:
 * 1. Match literal exato ou prefixo em código, ID ou nome (Score 1.0)
 * 2. Match de substring literal no nome ou categoria (Score 0.85 - 0.95)
 * 3. Match fonético em palavras do nome (Score 0.70 - 0.90)
 * 4. Match aproximado por distância de edição em palavras (Score 0.60 - 0.85)
 *
 * @param {string} queryTerm - Termo digitado pelo operador
 * @param {Array} indexedCatalog - Lista de itens pré-indexados com buildSearchItemIndex
 * @param {string} typeFilter - 'all' | 'products' | 'services'
 * @param {string} categoryFilter - 'all' | category_id | category_name
 * @returns {Array} Lista filtrada e ordenada por score decrescente
 */
const searchCatalogFuzzy = (queryTerm, indexedCatalog, typeFilter = 'all', categoryFilter = 'all') => {
  if (!Array.isArray(indexedCatalog)) return [];

  // Filtra por tipo de item primeiro
  let baseList = indexedCatalog;
  if (typeFilter === 'products') {
    baseList = indexedCatalog.filter((it) => it.typeItem === 'product');
  } else if (typeFilter === 'services') {
    baseList = indexedCatalog.filter((it) => it.typeItem === 'service');
  }

  // Filtra por categoria específica se selecionada
  if (categoryFilter && categoryFilter !== 'all') {
    const filterKey = String(categoryFilter);
    baseList = baseList.filter((it) => (
      it._categoryIdStr === filterKey ||
      it.categoryId === filterKey ||
      it._categoryName === normalizeRawText(filterKey) ||
      it.categoryName === filterKey
    ));
  }

  const cleanQuery = normalizeRawText(queryTerm);
  if (!cleanQuery) {
    return baseList;
  }

  const queryWords = cleanQuery.split(/\s+/).filter(Boolean);
  const queryPhoneticTokens = queryWords.map((w) => getPhoneticKey(w)).filter(Boolean);

  const scoredResults = [];

  for (let i = 0; i < baseList.length; i++) {
    const item = baseList[i];
    let score = 0;

    // 1. Verificação direta de ID ou Código de Barras
    if (item._idStr === cleanQuery || item._barCode === cleanQuery) {
      scoredResults.push({ item, score: 1.0 });
      continue;
    }

    if (item._barCode && item._barCode.includes(cleanQuery)) {
      score = Math.max(score, 0.95);
    }

    if (item._idStr && item._idStr.includes(cleanQuery)) {
      score = Math.max(score, 0.90);
    }

    // 2. Substring literal no nome (exato sem erro ortográfico)
    if (item._cleanName === cleanQuery) {
      score = Math.max(score, 0.99);
    } else if (item._cleanName.startsWith(cleanQuery)) {
      score = Math.max(score, 0.94);
    } else if (item._cleanName.includes(cleanQuery)) {
      score = Math.max(score, 0.88);
    }

    // 3. Match em palavras literais (se todas as palavras digitadas estiverem contidas)
    if (queryWords.length > 1) {
      const allWordsMatch = queryWords.every((qw) =>
        item._words.some((iw) => iw.includes(qw))
      );
      if (allWordsMatch) {
        score = Math.max(score, 0.90);
      }
    }

    // 4. Verificação Fonética & Aproximação (Fuzzy PT-BR)
    // Compara cada token fonético digitado contra os tokens fonéticos do produto
    let totalPhoneticScore = 0;
    let phoneticMatchesCount = 0;

    for (let qIdx = 0; qIdx < queryPhoneticTokens.length; qIdx++) {
      const qPhon = queryPhoneticTokens[qIdx];
      const qWord = queryWords[qIdx];
      let bestWordScore = 0;

      for (let wIdx = 0; wIdx < item._phoneticTokens.length; wIdx++) {
        const itemPhon = item._phoneticTokens[wIdx];
        const itemWord = item._words[wIdx];

        // 4.1 Chave fonética (ex: "reineken" / "raineke" / "heineken")
        if (qPhon && itemPhon) {
          if (qPhon === itemPhon) {
            bestWordScore = Math.max(bestWordScore, 0.95);
          } else if (qPhon.length >= 3 && itemPhon.startsWith(qPhon)) {
            // Termo digitado é prefixo do fonema do produto (ex: "raine" ou "heine")
            bestWordScore = Math.max(bestWordScore, 0.90);
          } else if (qPhon.startsWith(itemPhon) && Math.abs(qPhon.length - itemPhon.length) <= 1) {
            // Pequena omissão no fim do termo
            bestWordScore = Math.max(bestWordScore, 0.86);
          } else {
            // Similaridade fonética suave
            // Exige que o som inicial coincida para termos curtos (< 6 letras).
            // Isso impede que "choco" (xuku) case com "suco" (suku).
            const sameInitial = qPhon[0] === itemPhon[0];
            const isLongEnough = Math.min(qPhon.length, itemPhon.length) >= 6;
            if (sameInitial || isLongEnough) {
              const phonSim = getSimilarityScore(qPhon, itemPhon);
              if (phonSim >= 0.70) {
                bestWordScore = Math.max(bestWordScore, 0.82 * phonSim);
              }
            }
          }
        }

        // 4.2 Distância ortográfica direta da palavra (Damerau-Levenshtein)
        // Detecta erros de digitação comuns no nome (ex: "choclate" <-> "chocolate", "juelho" <-> "joelho")
        if (qWord && itemWord) {
          if (Math.abs(qWord.length - itemWord.length) <= 3) {
            const directSim = getSimilarityScore(qWord, itemWord);
            if (directSim >= 0.70) {
              bestWordScore = Math.max(bestWordScore, 0.90 * directSim);
            }
          }

          // 4.3 Tolerância para prefixo digitado incompleto (ex: digitou "choco", produto é "choclate")
          if (itemWord.length >= qWord.length && qWord.length >= 3) {
            const itemPrefix = itemWord.slice(0, qWord.length);
            const prefixSim = getSimilarityScore(qWord, itemPrefix);
            if (prefixSim >= 0.75) {
              bestWordScore = Math.max(bestWordScore, 0.88 * prefixSim);
            }
          }
        }
      }

      if (bestWordScore > 0) {
        totalPhoneticScore += bestWordScore;
        phoneticMatchesCount++;
      }
    }

    if (queryPhoneticTokens.length > 0 && phoneticMatchesCount > 0) {
      const avgPhoneticScore = totalPhoneticScore / queryPhoneticTokens.length;
      // Requer que a maioria das palavras digitadas tenha algum match razoável
      if (phoneticMatchesCount >= Math.ceil(queryPhoneticTokens.length * 0.7)) {
        score = Math.max(score, avgPhoneticScore);
      }
    }

    // 5. Categoria
    if (item._categoryName && item._categoryName.includes(cleanQuery)) {
      score = Math.max(score, 0.65);
    }

    // Se atingiu uma pontuação mínima relevante (>= 0.60), adiciona aos resultados
    if (score >= 0.60) {
      scoredResults.push({ item, score });
    }
  }

  // Ordena por score decrescente
  scoredResults.sort((a, b) => b.score - a.score);

  return scoredResults.map((res) => res.item);
};

export {
  normalizeRawText,
  getPhoneticKey,
  damerauLevenshteinDistance,
  getSimilarityScore,
  buildSearchItemIndex,
  searchCatalogFuzzy,
};
